import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { Award, Users, Globe, Heart, Shield, Microscope } from 'lucide-react';

const About = () => {
  const { t, isRTL } = useLanguage();

  const stats = [
    { number: '20+', label: t('yearsExperience') || 'Years of Experience' },
    { number: '500+', label: t('medicalProducts') || 'Medical Products' },
    { number: '100+', label: t('healthcarePartners') || 'Healthcare Partners' },
    { number: '50+', label: t('countries') || 'Countries Served' }
  ];

  const values = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: t('qualityFirst') || 'Quality First',
      description: t('qualityFirstDesc') || 'We maintain the highest standards in all our medical products and services.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: t('customerFocus') || 'Customer Focus',
      description: t('customerFocusDesc') || 'Our customers\' success and satisfaction drive everything we do.'
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: t('globalReach') || 'Global Reach',
      description: t('globalReachDesc') || 'Serving healthcare providers across multiple countries and regions.'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: t('healthcareCommitment') || 'Healthcare Commitment',
      description: t('healthcareCommitmentDesc') || 'Dedicated to improving patient outcomes through innovative solutions.'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: t('excellence') || 'Excellence',
      description: t('excellenceDesc') || 'Recognized for outstanding quality and service in the medical industry.'
    },
    {
      icon: <Microscope className="w-8 h-8" />,
      title: t('innovation') || 'Innovation',
      description: t('innovationDesc') || 'Continuously advancing medical technology and product development.'
    }
  ];

  return (
    <div className={`min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 ${isRTL ? 'font-arabic' : 'font-english'}`}>
      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`text-center ${isRTL ? 'text-right' : 'text-left'}`}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              {t('aboutTitle') || 'About Kuwait Chemicals'}
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              {t('aboutSubtitle') || 'Leading the way in medical products and pharmaceutical excellence across the Middle East and beyond.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="text-center text-white"
              >
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.number}</div>
                <div className="text-sm md:text-base text-primary-100">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t('ourMission') || 'Our Mission'}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                {t('missionText') || 'At Kuwait Chemicals, we are committed to providing high-quality medical products and pharmaceutical supplies to healthcare professionals and institutions. Our advanced verification system helps ensure the authenticity and quality of our medical products, maintaining the highest standards of safety and efficacy in healthcare.'}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                {t('missionText2') || 'We believe that access to reliable, high-quality medical products is fundamental to effective healthcare delivery. Through our comprehensive range of products and unwavering commitment to excellence, we support healthcare providers in delivering the best possible care to their patients.'}
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="aspect-w-16 aspect-h-10"
            >
              <img
                src="https://images.unsplash.com/photo-1559757175-0eb30cd8c063?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Medical professionals"
                className="rounded-2xl shadow-2xl object-cover w-full h-96"
              />
            </motion.div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="order-2 lg:order-1 aspect-w-16 aspect-h-10"
            >
              <img
                src="https://images.unsplash.com/photo-1582719471384-894fbb16e074?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Laboratory equipment"
                className="rounded-2xl shadow-2xl object-cover w-full h-96"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 }}
              className="order-1 lg:order-2"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t('whatWeDo') || 'What We Do'}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                {t('whatWeDoText') || 'We specialize in manufacturing and distributing medical products, pharmaceutical supplies, and healthcare equipment. Our comprehensive product catalog includes medications, medical devices, laboratory supplies, and specialized healthcare solutions.'}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                {t('whatWeDoText2') || 'Each product comes with unique verification codes to ensure authenticity and quality assurance. We work closely with healthcare professionals to understand their needs and provide tailored solutions that meet the highest medical standards.'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('ourValues') || 'Our Values'}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('valuesSubtitle') || 'The principles that guide everything we do'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="text-primary-600 mb-4">{value.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('partnWithUs') || 'Partner With Us'}
            </h2>
            <p className="text-xl text-primary-100 mb-8 leading-relaxed">
              {t('partnWithUsDesc') || 'Join our network of healthcare partners and experience the Kuwait Chemicals difference in medical product excellence.'}
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-primary-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-gray-100 transition-colors shadow-lg mx-auto max-w-full"
            >
              {t('contactUs') || 'Contact Us Today'}
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
